import { LogLevel, Category } from './DataModels';

function getNotificationHandler() {
  const _listeners: Array<(args: INotification) => void> = [];
  return {
    /**
     * Fires an event to its listeners
     * @param args - The parameters of the event
     */
    fireEvent(args: INotification): void {
      _listeners.forEach(listener => listener(args));
    },

    /**
     * Add an event listener
     * @param listener - The listener that can process events
     */
    addListener(listener: (args: INotification) => void): void {
      if (listener) {
        _listeners.push(listener);
      }
    }
  };
}

let notificationHandler = getNotificationHandler();

// This interface hides fireEvent from callers in TypeScript
export interface INotificationHandler {
  addListener(listener: (args: INotification) => void): void;
}

export interface INotification {
  level: LogLevel;
  category: Category;
  message: () => string;
}

/**
 * Returns a list of notification event listeners. You can add an event listener:
 *    onNotification().addListener(event =\> \{
 *      console.log(event!.message + ' ' + event!.level); \});
 *
 * DO NOT trigger telemetry events on these notifications, as doing so may
 * create an infinite loop of telemetry events.
 */
export function onNotification(): INotificationHandler {
  return notificationHandler;
}

/**
 * Internal function: log a notification
 * @param level - The level of notification to log
 * @param category - The category of the notification
 * @param message - A function that returns a message to log
 */
export function logNotification(level: LogLevel, category: Category, message: () => string) {
  notificationHandler.fireEvent({ level, category, message });
}

/**
 * Internal function: log an error
 * @param category - The category of the notification
 * @param message - A message that will preceed the message from error object
 * @param error - An error object, from which an error message might be obtained
 */
export function logError(category: Category, message: string, error: any) {
  logNotification(LogLevel.Error, category, () => {
    let errorMessage = error instanceof Error ? error.message : '';
    return `${message}: ${errorMessage}`;
  });
}
